import { useEffect, useState, type ReactNode } from 'react'
import { pullCategories } from '@/features/inventory/pullCategories'
import { pullChanges } from '@/offline/pull'
import { ensureWorkspace } from './workspace'

type Status = 'loading' | 'ready' | 'error'

/** Provisions the owner's workspace once after sign-in, then renders the app. */
export function RequireWorkspace({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    ensureWorkspace()
      .then((wsId) => {
        // Non-blocking initial pulls so a returning/second device reconciles from the server.
        void pullCategories(wsId).catch(() => {
          /* offline or not permitted yet — categories will pull later */
        })
        void pullChanges(wsId).catch(() => {
          /* offline — user can Sync now from /sync */
        })
        if (mounted) setStatus('ready')
      })
      .catch((e: unknown) => {
        if (!mounted) return
        setError(e instanceof Error ? e.message : String(e))
        setStatus('error')
      })
    return () => {
      mounted = false
    }
  }, [])

  if (status === 'loading') {
    return <Centered>Preparing your workspace…</Centered>
  }
  if (status === 'error') {
    return <Centered>Could not prepare workspace: {error}</Centered>
  }
  return <>{children}</>
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-950 px-6 text-center text-sm text-slate-300">
      {children}
    </div>
  )
}
